import * as React from 'react';
import { __ } from 'react-i18n';
import { FlexCol } from '../../Defaults/Flexbox/Flexbox';
import styled from 'styled-components';
import { rem } from 'polished';
import { TitleWithChildren } from '../General/TitleWithChildren';
import { ContactInfo } from './Form/ContactInfo';
import { BillingAddress } from './Form/BillingAddress';
import { DeliveryAddress } from './Form/DeliveryAddress';
import { FormTextArea } from '../Form/FormTextArea';
import { prop } from '../../../utilities';
import { InfoText, InfoType } from '../General/InfoText';

interface Props {
  data: any;
  updateCart: (data: any) => void;
  deliveryAddresses: any;
  dispatchCartContactInfo: (data: {
    field: string;
    value: string | number;
  }) => {};
  cartContactInfo: any;
  showProblems: boolean;
}

function CartContactInfoPart({
  data,
  updateCart,
  deliveryAddresses,
  showProblems,
  dispatchCartContactInfo,
  cartContactInfo,
}: Props) {
  const problems = showProblems ? data.problems : null;

  const initialState = getInitialStateFromCartData(cartContactInfo);
  const { use_delivery_address, billing_company, note } = initialState;

  const stateRef = React.useRef(cartContactInfo);
  React.useEffect(() => {
    stateRef.current = cartContactInfo;
  }, [cartContactInfo]);

  const foundFormProblems =
    !problems || problems.length === 0
      ? null
      : problems.filter(p => p.type === 'form');
  const formProblems = foundFormProblems
    ? prop(foundFormProblems[0], 'payload')
    : null;

  const handleFieldChange = (e: any) => {
    const value = e.target.value;
    const field = e.target.name;
    dispatchCartContactInfo({ field, value });
  };

  const handleBlur = () => {
    const info = stateRef.current;
    updateCart({
      ...data,
      billing_address: info.billing_address,
      company: info.company,
      delivery_address: info.delivery_address,
      note: info.note,
    });
  };

  return (
    <>
      {formProblems && (
        <InfoText
          maxWidth={1168}
          type={InfoType.ERROR}
          info={`Opravte prosím chyby v údajoch`}
        />
      )}
      <form>
        <Wrapper>
          <TitleWithChildren title={__('KONTAKTNÉ ÚDAJE')}>
            <ContactInfo
              initialInfo={{
                email: initialState.email,
                phone: initialState.phone,
              }}
              onBlur={handleBlur}
              formProblems={formProblems}
              handleChange={dispatchCartContactInfo}
            />
          </TitleWithChildren>
          <TitleWithChildren title={__('FAKTURAČNÁ ADRESA')}>
            <BillingAddress
              isCompany={false}
              billingAddress={prop(cartContactInfo, 'billing_address')}
              company={prop(cartContactInfo, 'company')}
              billing_company={prop(cartContactInfo, 'billing_company')}
              use_delivery_address={prop(
                cartContactInfo,
                'use_delivery_address',
              )}
              onBlur={handleBlur}
              formProblems={formProblems}
              handleChange={dispatchCartContactInfo}
            />
          </TitleWithChildren>
          {use_delivery_address && (
            <TitleWithChildren title={__('DODACIA ADRESA')}>
              <DeliveryAddress
                deliveryAddresses={deliveryAddresses}
                deliveryAddress={prop(cartContactInfo, 'delivery_address')}
                onBlur={handleBlur}
                formProblems={formProblems}
                handleChange={dispatchCartContactInfo}
              />
            </TitleWithChildren>
          )}
          <FormTextArea
            name="note"
            type="text"
            label={__('Poznámka')}
            defaultValue={note}
            onChange={handleFieldChange}
            onBlur={handleBlur}
          />
        </Wrapper>
      </form>
    </>
  );
}

function reducer(state, { field, value }) {
  return { ...state, [field]: value };
}

const getInitialStateFromCartData = (data: any) => {
  return {
    email: prop(data, 'billing_address.person.email', ''),
    phone: prop(data, 'billing_address.person.phone', ''),
    use_delivery_address: prop(data, 'use_delivery_address', false),
    billing_company: prop(data, 'billing_company', false),
    note: prop(data, 'note', ''),
  };
};

const Wrapper = styled(FlexCol)`
  max-width: ${rem(700)};
  width: 100%;

  ${({ theme }) => theme.mediab.l925`
    padding-top: ${rem(56)};
  `}
`;

export { CartContactInfoPart };
